import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { AdminTableIconComponent } from './table-icon.component';
import { clampPage, pageCount, pageReport, pageWindow, TablePage } from './table.util';

/** Page links, first/prev/next/last, rows-per-page select and the "Showing x to y" line. */
@Component({
  selector: 'admin-paginator',
  standalone: true,
  imports: [AdminTableIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (itemLabel) {
      <span class="current">{{ report }}</span>
    }
    <button type="button" aria-label="First Page" [disabled]="page <= 1" (click)="go(1)">
      <admin-table-icon name="angleDoubleLeft"></admin-table-icon>
    </button>
    <button type="button" aria-label="Previous Page" [disabled]="page <= 1" (click)="go(page - 1)">
      <admin-table-icon name="angleLeft"></admin-table-icon>
    </button>
    <span class="pages">
      @for (p of links; track p) {
        <button
          type="button"
          [class.selected]="p === page"
          [attr.aria-current]="p === page ? 'page' : null"
          [attr.aria-label]="p"
          (click)="go(p)"
        >
          {{ p }}
        </button>
      }
    </span>
    <button type="button" aria-label="Next Page" [disabled]="page >= pages" (click)="go(page + 1)">
      <admin-table-icon name="angleRight"></admin-table-icon>
    </button>
    <button type="button" aria-label="Last Page" [disabled]="page >= pages" (click)="go(pages)">
      <admin-table-icon name="angleDoubleRight"></admin-table-icon>
    </button>
    @if (pageSizeOptions.length) {
      <span class="rpp">
        <select aria-label="Rows per page" [value]="pageSize" (change)="resize($any($event.target).value)">
          @for (size of pageSizeOptions; track size) {
            <option [value]="size" [selected]="size === pageSize">{{ size }}</option>
          }
        </select>
        <admin-table-icon name="chevronDown"></admin-table-icon>
      </span>
    }
  `,
  styles: [
    `
      :host {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: center;
        gap: 4px;
        padding: 8px 16px;
        background: #fff;
        color: #334155;
        border-radius: 6px;
      }
      .current {
        color: #64748b;
      }
      .pages {
        display: flex;
        align-items: center;
        gap: 4px;
      }
      button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 40px;
        min-width: 40px;
        height: 40px;
        padding: 0;
        border: 0;
        border-radius: 50%;
        background: transparent;
        color: #64748b;
        font: inherit;
        line-height: 16px;
        cursor: pointer;
        transition:
          background 0.2s,
          color 0.2s,
          box-shadow 0.2s;
      }
      button:not(:disabled):not(.selected):hover {
        background: #f1f5f9;
        color: #475569;
      }
      button:disabled {
        opacity: 0.6;
        cursor: default;
      }
      button.selected {
        background: #ecfdf5;
        color: #047857;
      }
      button:focus-visible,
      select:focus-visible {
        outline: 1px solid #10b981;
        outline-offset: 2px;
      }
      button admin-table-icon {
        width: 16px;
        height: 16px;
      }
      .rpp {
        position: relative;
        display: inline-flex;
        border: 1px solid #cbd5e1;
        border-radius: 6px;
        background: #fff;
      }
      .rpp:hover {
        border-color: #94a3b8;
      }
      select {
        appearance: none;
        field-sizing: content;
        border: 0;
        background: transparent;
        color: #334155;
        font: inherit;
        line-height: 22.4px;
        padding: 10px 52px 10px 12px;
        border-radius: 6px;
        cursor: pointer;
      }
      .rpp admin-table-icon {
        position: absolute;
        right: 12px;
        top: 50%;
        width: 16px;
        height: 16px;
        margin-top: -8px;
        color: #64748b;
        pointer-events: none;
      }
    `,
  ],
})
export class AdminPaginatorComponent {
  /** 1-based. */
  @Input() page = 1;
  @Input() pageSize = 50;
  @Input() total = 0;
  @Input() pageSizeOptions: number[] = [];
  /** Noun for the report line ("users"); no report line when empty. */
  @Input() itemLabel = '';
  @Output() readonly pageChange = new EventEmitter<TablePage>();

  /** 0 when there are no rows, so no page links show (as in PrimeNG). */
  get pages(): number {
    return this.total > 0 ? pageCount(this.total, this.pageSize) : 0;
  }

  get links(): number[] {
    return pageWindow(this.page, this.pages);
  }

  get report(): string {
    return pageReport(this.page, this.pageSize, this.total, this.itemLabel);
  }

  go(page: number): void {
    const next = clampPage(page, this.total, this.pageSize);
    if (next !== this.page) this.pageChange.emit({ page: next, pageSize: this.pageSize });
  }

  resize(value: string): void {
    const pageSize = Number(value);
    if (pageSize && pageSize !== this.pageSize) this.pageChange.emit({ page: 1, pageSize });
  }
}
